import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "light" | "dark" | "midnight";

const STORAGE_KEY = "goyaz-theme";
const DEFAULT_THEME: Theme = "light";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  themes: { key: Theme; label: string; description: string }[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const THEMES: ThemeContextValue["themes"] = [
  { key: "light", label: "Claro", description: "Tradicional, alto contraste" },
  { key: "dark", label: "Escuro", description: "Cinza azulado, suave pros olhos" },
  { key: "midnight", label: "Midnight", description: "Azul-quase-preto com toque roxo" },
];

function isTheme(v: string | null): v is Theme {
  return v === "light" || v === "dark" || v === "midnight";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === "undefined") return DEFAULT_THEME;
    const saved = localStorage.getItem(STORAGE_KEY);
    return isTheme(saved) ? saved : DEFAULT_THEME;
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    // Mantém class="dark" pra compatibilidade com `dark:` do Tailwind também
    document.documentElement.classList.toggle("dark", theme === "dark" || theme === "midnight");
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeState, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme deve ser usado dentro de ThemeProvider");
  return ctx;
}
